import React, { useMemo, useState } from 'react';
import { Copy, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { claimsAPI } from '../services/apiService';
import {
  getGeneralQuestions,
  getItemQuestions,
  getVisibleQuestions,
  pruneAnswersToVisible,
  countUnanswered,
  loadCaseBrief,
  buildCaseSummaryText,
  buildAllEscalationDrafts
} from '../services/caseBriefService';

const parseJson = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const ChoiceField = ({ label, value, options, onChange }) => (
  <div>
    <label className="field-label text-left">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
    >
      <option value="">Select…</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const ClaimBriefEscalateModal = ({ claim, member, onClose, onSuccess }) => {
  const seeded = useMemo(() => loadCaseBrief(claim), [claim]);
  const [tab, setTab] = useState('questions');
  const [general, setGeneral] = useState(seeded.general);
  const [itemAnswers, setItemAnswers] = useState(seeded.itemAnswers);
  const [saving, setSaving] = useState(false);
  const items = seeded.items;
  const roadmapItems = seeded.roadmapItems || items;
  const generalQuestions = useMemo(() => getGeneralQuestions(items), [items]);

  const visibleGeneral = useMemo(
    () => getVisibleQuestions(generalQuestions, general),
    [generalQuestions, general]
  );

  const summaryText = useMemo(
    () => buildCaseSummaryText({
      member,
      claim,
      general,
      items,
      itemAnswers,
      roadmapItems
    }),
    [member, claim, general, items, itemAnswers, roadmapItems]
  );

  const drafts = useMemo(
    () => buildAllEscalationDrafts({ member, claim, general, items, itemAnswers }),
    [member, claim, general, items, itemAnswers]
  );

  const setGeneralAnswer = (qid, value) => {
    setGeneral((prev) => pruneAnswersToVisible(generalQuestions, { ...prev, [qid]: value }));
  };

  const setItemAnswer = (item, qid, value) => {
    const questions = getItemQuestions(item.factorKey, item);
    setItemAnswers((prev) => ({
      ...prev,
      [item.id]: pruneAnswersToVisible(questions, {
        ...(prev[item.id] || {}),
        [qid]: value
      })
    }));
  };

  const unansweredGeneral = countUnanswered(generalQuestions, general);
  const unansweredItems = items.reduce((sum, item) => {
    const questions = getItemQuestions(item.factorKey, item);
    return sum + countUnanswered(questions, itemAnswers[item.id] || {});
  }, 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const pricing = parseJson(claim.pricing_data, {}) || {};
      const caseBrief = {
        general,
        itemAnswers,
        summaryText,
        drafts: drafts.map((d) => ({ id: d.id, title: d.title, to: d.to, draft: d.draft })),
        updatedAt: new Date().toISOString()
      };
      await claimsAPI.update(claim.id, {
        ...claim,
        pricing_data: {
          ...pricing,
          caseBrief
        }
      });
      toast.success('Brief & drafts saved');
      onSuccess?.();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save brief');
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const tabs = [
    { id: 'questions', label: 'Questions' },
    { id: 'summary', label: 'Case roadmap' },
    { id: 'drafts', label: `Escalate drafts (${drafts.length})` }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-5">
      <div
        className="bg-white rounded-xl shadow-2xl w-full flex flex-col"
        style={{ width: 'min(980px, 96vw)', height: 'min(780px, 94vh)' }}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Brief & Escalate</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Claim #{claim.id} · Pick the PF service · Roadmap + point-wise drafts (no claim ID in letter)
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-3 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 pb-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                tab === t.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
          {(unansweredGeneral + unansweredItems) > 0 && tab === 'questions' && (
            <span className="text-[11px] text-amber-700 self-center ml-1">
              {unansweredGeneral + unansweredItems} answer(s) still empty
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-5">
          {tab === 'questions' && (
            <div className="space-y-6">
              <section className="rounded-xl border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">A. About this case</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {visibleGeneral.map((q) => (
                    <ChoiceField
                      key={q.id}
                      label={q.label}
                      value={general[q.id]}
                      options={q.options}
                      onChange={(val) => setGeneralAnswer(q.id, val)}
                    />
                  ))}
                </div>
                {general.needs_followup === 'No' && (
                  <p className="mt-3 text-xs text-slate-500">
                    Follow-up not needed — further case questions are hidden.
                  </p>
                )}
              </section>

              {general.needs_followup !== 'No' && (
                <section className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-900">
                    B. Pending PF services (Completed hidden)
                  </h4>
                  {items.length === 0 ? (
                    <p className="text-sm text-slate-500 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      No open items to ask about. All in-scope activities are Completed (or none set).
                    </p>
                  ) : (
                    items.map((item) => {
                      const questions = getItemQuestions(item.factorKey, item);
                      const answers = itemAnswers[item.id] || {};
                      const visible = getVisibleQuestions(questions, answers);
                      return (
                        <div key={item.id} className="rounded-xl border border-slate-200 p-4">
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-slate-900">
                              {item.instanceLabel
                                ? `${item.label} — ${item.instanceLabel}`
                                : item.label}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              System status: {item.status}
                            </p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {visible.map((q) => (
                              <ChoiceField
                                key={q.id}
                                label={q.label}
                                value={answers[q.id]}
                                options={q.options}
                                onChange={(val) => setItemAnswer(item, q.id, val)}
                              />
                            ))}
                          </div>
                          {answers.blocked === 'No' && (
                            <p className="mt-3 text-xs text-slate-500">
                              Not blocked — further questions for this service are hidden.
                            </p>
                          )}
                        </div>
                      );
                    })
                  )}
                </section>
              )}
            </div>
          )}

          {tab === 'summary' && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => copyText(summaryText, 'Roadmap')}
                  className="btn-secondary inline-flex items-center text-sm"
                >
                  <Copy className="w-4 h-4 mr-1.5" />
                  Copy roadmap
                </button>
              </div>
              <pre className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800 leading-relaxed">
                {summaryText}
              </pre>
            </div>
          )}

          {tab === 'drafts' && (
            <div className="space-y-4">
              {drafts.length === 0 ? (
                <p className="text-sm text-slate-500 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  No drafts yet. Confirm the PF service, mark it blocked, then set escalate letter = Yes.
                </p>
              ) : (
                drafts.map((d) => (
                  <div key={d.id} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{d.title}</p>
                        <p className="text-xs text-slate-500">To: {d.to}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyText(d.draft, 'Draft')}
                        className="btn-secondary inline-flex items-center text-sm"
                      >
                        <Copy className="w-4 h-4 mr-1.5" />
                        Copy draft
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-800 leading-relaxed">
                      {d.draft}
                    </pre>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 shrink-0 bg-slate-50/80">
          <p className="text-xs text-slate-500">
            {seeded.updatedAt
              ? `Last saved: ${new Date(seeded.updatedAt).toLocaleString()}`
              : 'Not saved yet'}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">Close</button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="btn-primary inline-flex items-center disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving…' : 'Save brief'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimBriefEscalateModal;
